import styled from 'styled-components';
import {match} from 'ts-pattern';

import {CSSProperties, Fragment} from 'react';

import {isNil} from 'ramda';

import {FinancingFieldResponseBody} from '@dms/api/core';
import {CompositionFont} from '@dms/api/sales';
import {
  CompositionTextLayer,
  FinancingCompositionLayer,
  FinancingCompositionScene,
} from '@dms/api/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useLayerDrag} from '../hooks/useLayerDrag';
import {LayerBox} from '../types/CompositionEditorAction';
import {RESIZE_HANDLES} from '../types/ResizeHandle';
import {getCompositionFontFamily} from '../utils/getCompositionFontFamily';
import {renderTextWithSamples} from '../utils/renderTextWithSamples';
import {toCssPx} from '../utils/toCssPx';
import {CroppedReferencePhoto} from './CroppedReferencePhoto';
import {LayerResizeHandle} from './LayerResizeHandle';

interface CanvasLayerProps extends TestIdProps {
  layer: FinancingCompositionLayer;
  canvas: FinancingCompositionScene['canvas'];
  scale: number;
  isSelected: boolean;
  fonts: CompositionFont[];
  // Financing mode only: the data-field catalog whose samples replace `{{KEY}}` tokens.
  financingFields: FinancingFieldResponseBody[];
  imageUrl: string | null;
  onSelect: () => void;
  onCommit: (box: LayerBox) => void;
}

// Layer geometry and paint come straight from the scene (raster px, hex colors), so they are
// applied as computed inline styles via `.attrs` rather than as design-system tokens.
const LayerFrame = styled.div.attrs<{$box: LayerBox; $scale: number}>((props) => ({
  style: {
    left: toCssPx(props.$box.x * props.$scale),
    top: toCssPx(props.$box.y * props.$scale),
    width: toCssPx(props.$box.width * props.$scale),
    height: toCssPx(props.$box.height * props.$scale),
  },
}))<{$box: LayerBox; $scale: number; $isSelected: boolean}>`
  position: absolute;
  box-sizing: border-box;
  cursor: move;
  touch-action: none;
  user-select: none;
  outline: ${(props) =>
    props.$isSelected ? `1px solid ${props.theme.colors.general.accent}` : 'none'};
  outline-offset: -1px;
`;

const LayerContent = styled.div.attrs<{$opacity: number}>((props) => ({
  style: {opacity: props.$opacity},
}))<{$opacity: number}>`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const PaintedContent = styled.div.attrs<{$style: CSSProperties}>((props) => ({
  style: props.$style,
}))<{$style: CSSProperties}>`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  white-space: pre-wrap;
  word-break: break-word;
`;

// A flex column whose auto margins place the text block vertically. Auto margins never go below
// zero, so text taller than the layer starts at the top, the same as the server render.
const TextFrame = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const TextBlock = styled.div.attrs<{$style: CSSProperties}>((props) => ({
  style: props.$style,
}))<{$style: CSSProperties}>`
  flex-shrink: 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

// A data field without a sample value; it follows the layer's font size like the text around it.
const FieldChip = styled.span`
  padding: 0 0.25em;
  border-radius: 0.25em;
  background: ${(props) => props.theme.colors.palettes.blue[20][100]};
  color: ${(props) => props.theme.colors.text.primary};
`;

const getVerticalAlignMargins = (
  verticalAlign: CompositionTextLayer['verticalAlign']
): CSSProperties =>
  match(verticalAlign)
    .with('middle', () => ({marginTop: 'auto', marginBottom: 'auto'}))
    .with('bottom', () => ({marginTop: 'auto'}))
    .otherwise(() => ({}));

const ImageContent = styled.img.attrs<{$fit: CSSProperties['objectFit']}>((props) => ({
  style: {objectFit: props.$fit},
}))<{$fit: CSSProperties['objectFit']}>`
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  border: 1px dashed ${(props) => props.theme.colors.general.accent};
  box-sizing: border-box;
`;

// The vehicle photo slot: the BE fills it per vehicle, so the editor shows the reference photo in
// it, cropped and fitted like the vehicle photo. The dashed frame is drawn over the photo, so it
// does not shrink the box the photo is fitted to.
const PhotoSlot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.colors.palettes.neutral[30][100]};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px dashed ${(props) => props.theme.colors.general.accent};
    pointer-events: none;
  }
`;

export function CanvasLayer(props: CanvasLayerProps) {
  // A touch drag selects on its first move and sends no mousedown, so the focus stays in the
  // inspector field of the previous layer. Blur it first so it commits its draft before the
  // selection change unmounts it.
  const select = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    props.onSelect();
  };

  const drag = useLayerDrag({
    layer: props.layer,
    canvas: props.canvas,
    scale: props.scale,
    onSelect: select,
    onCommit: props.onCommit,
  });

  const content = match(props.layer)
    .with({type: 'text'}, (layer) => (
      <TextFrame>
        <TextBlock
          $style={{
            ...getVerticalAlignMargins(layer.verticalAlign),
            fontFamily: getCompositionFontFamily(layer.fontId, props.fonts),
            fontSize: toCssPx(layer.fontSize * props.scale),
            fontWeight: layer.fontWeight,
            color: layer.color,
            textAlign: layer.align,
            lineHeight: layer.lineHeight,
          }}
          data-testid={suffixTestId(`text-${layer.id}`, props)}
        >
          {renderTextWithSamples(layer.text, props.financingFields).map((segment) =>
            segment.type === 'text' ? (
              <Fragment key={segment.offset}>{segment.value}</Fragment>
            ) : (
              <FieldChip key={segment.offset}>{segment.label}</FieldChip>
            )
          )}
        </TextBlock>
      </TextFrame>
    ))
    .with({type: 'image'}, (layer) =>
      isNil(props.imageUrl) ? (
        <ImagePlaceholder />
      ) : (
        <ImageContent src={props.imageUrl} alt="" draggable={false} $fit={layer.fit} />
      )
    )
    .with({type: 'rect'}, (layer) => (
      <PaintedContent
        $style={{
          background: layer.fill,
          borderRadius: toCssPx(layer.radius * props.scale),
          border: isNil(layer.stroke)
            ? 'none'
            : `${toCssPx(layer.stroke.width * props.scale)} solid ${layer.stroke.color}`,
        }}
      />
    ))
    .with({type: 'photo'}, (layer) => (
      <PhotoSlot data-testid={suffixTestId(`photoSlot-${layer.fit}`, props)}>
        <CroppedReferencePhoto
          crop={layer.crop}
          fit={layer.fit}
          width={drag.box.width * props.scale}
          height={drag.box.height * props.scale}
          data-testid={suffixTestId(`photo-${layer.id}`, props)}
        />
      </PhotoSlot>
    ))
    .exhaustive();

  return (
    <LayerFrame
      {...drag.bindMove()}
      $box={drag.box}
      $scale={props.scale}
      $isSelected={props.isSelected}
      onClick={(event) => {
        event.stopPropagation();
        select();
      }}
      data-testid={suffixTestId(`layer-${props.layer.id}`, props)}
    >
      <LayerContent $opacity={props.layer.opacity}>{content}</LayerContent>
      {props.isSelected &&
        RESIZE_HANDLES.map((handle) => (
          <LayerResizeHandle
            key={handle}
            {...drag.bindResize()}
            $handle={handle}
            data-handle={handle}
            data-testid={suffixTestId(`handle-${handle}`, props)}
          />
        ))}
    </LayerFrame>
  );
}
